/**
 * Performance monitoring utilities for the bowling league application
 */

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initializeObservers();
  }

  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        const navObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.recordMetric('page_load_time', navEntry.loadEventEnd - navEntry.fetchStart);
              this.recordMetric('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.fetchStart);
              this.recordMetric('first_paint', navEntry.loadEventEnd - navEntry.fetchStart);
            }
          }
        });
        navObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navObserver);
      } catch (e) {
        console.warn('Navigation timing observer not supported');
      }

      // Observe resource timing
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'resource') {
              const resourceEntry = entry as PerformanceResourceTiming;
              if (resourceEntry.name.includes('supabase') || resourceEntry.name.includes('api')) {
                this.recordMetric('api_request_time', resourceEntry.responseEnd - resourceEntry.requestStart, {
                  url: resourceEntry.name,
                  size: resourceEntry.transferSize
                });
              }
            }
          }
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);
      } catch (e) {
        console.warn('Resource timing observer not supported');
      }

      // Observe largest contentful paint
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.recordMetric('largest_contentful_paint', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);
      } catch (e) {
        console.warn('LCP observer not supported');
      }

      // Observe first input delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const fidEntry = entry as any; // PerformanceEventTiming
            if (fidEntry.processingStart) {
              this.recordMetric('first_input_delay', fidEntry.processingStart - entry.startTime);
            }
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);
      } catch (e) {
        console.warn('FID observer not supported');
      }
    }
  }

  /**
   * Record a custom performance metric
   */
  recordMetric(name: string, value: number, metadata?: Record<string, any>) {
    this.metrics.push({
      name,
      value,
      timestamp: Date.now(),
      metadata
    });

    // Keep only last 100 metrics to prevent memory leaks
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    // Log significant performance issues
    if (this.shouldLogMetric(name, value)) {
      console.warn(`Performance issue detected: ${name} = ${value}ms`, metadata);
    }
  }

  private shouldLogMetric(name: string, value: number): boolean {
    const thresholds: Record<string, number> = {
      'page_load_time': 3000,
      'api_request_time': 2000,
      'largest_contentful_paint': 2500,
      'first_input_delay': 100,
      'component_render_time': 16, // 60fps = 16ms per frame
    };

    return value > (thresholds[name] || Infinity);
  }

  /**
   * Get performance metrics summary
   */
  getMetricsSummary() {
    const summary: Record<string, { avg: number; max: number; count: number }> = {};

    for (const metric of this.metrics) {
      if (!summary[metric.name]) {
        summary[metric.name] = { avg: 0, max: 0, count: 0 };
      }

      const current = summary[metric.name];
      current.count++;
      current.max = Math.max(current.max, metric.value);
      current.avg = (current.avg * (current.count - 1) + metric.value) / current.count;
    }

    return summary;
  }

  /**
   * Get recent metrics
   */
  getRecentMetrics(minutes: number = 5): PerformanceMetric[] {
    const cutoff = Date.now() - (minutes * 60 * 1000);
    return this.metrics.filter(metric => metric.timestamp > cutoff);
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = [];
  }

  /**
   * Cleanup observers
   */
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Higher-order component to measure component render time
 * Note: This is a placeholder implementation for TypeScript compatibility
 */
export function withPerformanceTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  _componentName: string
) {
  return function PerformanceTrackedComponent(_props: P) {
    // For now, return the component without tracking
    // This would be implemented properly when used in React components
    return WrappedComponent as any;
  };
}

/**
 * Hook to measure async operation performance
 * Note: This should be used within React components that have React imported
 */
export function createPerformanceTracker(operationName: string) {
  return {
    startTracking: () => {
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        performanceMonitor.recordMetric(
          `${operationName}_duration`,
          endTime - startTime,
          { operation: operationName }
        );
      };
    }
  };
}

/**
 * Measure database query performance
 */
export async function measureQueryPerformance<T>(
  queryName: string,
  queryFunction: () => Promise<T>
): Promise<T> {
  const startTime = performance.now();
  
  try {
    const result = await queryFunction();
    const endTime = performance.now();
    
    performanceMonitor.recordMetric(
      'database_query_time',
      endTime - startTime,
      { query: queryName, success: true }
    );
    
    return result;
  } catch (error) {
    const endTime = performance.now();
    
    performanceMonitor.recordMetric(
      'database_query_time',
      endTime - startTime,
      { query: queryName, success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    );
    
    throw error;
  }
}

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    performanceMonitor.cleanup();
  });
}